import type { z } from 'zod';

/**
 * The api is the same origin as the app (`/v1`), reached with the session
 * cookies the browser holds — the SPA never sees a credential. A 401 is
 * answered once by a refresh and a retry; a second 401 means "sign in".
 * That first retry applies to **every** door except the three that establish
 * the session itself — `/v1/auth/me` above all, which is what every page load
 * asks first.
 * Every response is parsed through the synced contract, so a field the api
 * stopped sending fails here, loudly, rather than rendering as a blank.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Where the api's non-/v1 doors live (Google sign-in). Same origin unless told otherwise. */
export const API_ORIGIN: string = import.meta.env.VITE_API_ORIGIN ?? '';

let refreshing: Promise<boolean> | null = null;
function refresh(): Promise<boolean> {
  refreshing ??= fetch(`${API_ORIGIN}/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'x-ptitim-web': '1' },
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/**
 * The doors that must never answer a 401 by refreshing.
 *
 * ⚠ This used to be the prefix `/v1/auth/`, which was meant to stop the
 * refresh from recursing and instead caught **`/v1/auth/me`** — the first call
 * the app makes on every load. An access cookie lives fifteen minutes, so
 * anybody who came back to the tab after that got a 401 nobody tried to
 * refresh, and was sent to the start page to sign in again with a perfectly
 * good session in their browser. Vladimir, 2026-09-21: *"after each release I
 * am redirected to the start page and need to sign in with google again for
 * some reason, that is definitely a bug"* — a release is just the commonest
 * reason to leave a tab alone and come back to it.
 */
const NEVER_REFRESHED = ['/v1/auth/refresh', '/v1/auth/logout', '/v1/auth/google'];

type Init = Omit<RequestInit, 'body'> & { json?: unknown };

async function send(path: string, init: Init, retried = false): Promise<Response> {
  const headers = new Headers(init.headers);
  // The api refuses a cookie-authenticated mutation without this header
  // (`WebOriginGuard`, its CSRF close): a custom header can only come from our
  // own script, never from a form, an image tag or a navigation.
  headers.set('x-ptitim-web', '1');
  let body: BodyInit | undefined;
  if (init.json !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(init.json);
  }
  const res = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    headers,
    body,
    credentials: 'include',
  });
  if (res.status === 401 && !retried && !NEVER_REFRESHED.some((p) => path.startsWith(p))) {
    if (await refresh()) return send(path, init, true);
  }
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const parsed = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(parsed.message)) message = parsed.message.join('; ');
      else if (typeof parsed.message === 'string') message = parsed.message;
    } catch {
      /* no body */
    }
    throw new ApiError(res.status, message);
  }
  return res;
}

export async function api<S extends z.ZodTypeAny>(
  schema: S,
  path: string,
  init: Init = {},
): Promise<z.output<S>> {
  const res = await send(path, init);
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  return schema.parse(data) as z.output<S>;
}

export const isNotSignedIn = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

/** 402: the free month is over, and the message says whom to write to (2026-09-26). */
export const isFreeMonthOver = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.status === 402;
